import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { useLanguage } from '../../../contexts/LanguageContext';
import useVehiclesForSale, { TAB_KEYS } from './hooks/useVehiclesForSale';
import SaleListingsFilter from './components/SaleListingsFilter';
import SaleListingsTable from './components/SaleListingsTable';
import MyPurchaseRequestsFilter from './components/MyPurchaseRequestsFilter';
import MyPurchaseRequestsTable from './components/MyPurchaseRequestsTable';
import ListingDetailDialog from './components/ListingDetailDialog';
import CreatePurchaseRequestDialog from './components/CreatePurchaseRequestDialog';
import { useCancelPurchaseRequestMutation } from '../../../api/endpoints/vehicleSale';
import { toastService } from '../../../utils/toastService';

const VehiclesForSalePage = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();
    const {
        activeTabIndex,
        setActiveTabIndex,
        activeTabKey,
        tabConfig,
        listingFilters,
        requestFilters,
        listings,
        listingsTotal,
        requests,
        requestsTotal,
        isLoading,
        handleApplyListingFilters,
        handleResetListingFilters,
        handleApplyRequestFilters,
        handleResetRequestFilters,
        handleListingPageChange,
        handleRequestPageChange,
        refetch
    } = useVehiclesForSale();

    const [detailListingId, setDetailListingId] = useState(null);
    const [purchaseTarget, setPurchaseTarget] = useState(null);

    const [cancelPurchaseRequest] = useCancelPurchaseRequestMutation();

    const handleViewDetail = (row) => setDetailListingId(row.listingId);
    const handleDetailClose = () => setDetailListingId(null);

    const handleCreatePurchaseRequest = (row) => setPurchaseTarget(row);
    const handleCreateClose = () => setPurchaseTarget(null);

    const handleCancelMyRequest = (row) => {
        confirmDialog({
            header: th('vehiclesForSale.dialog.confirmCancelRequestTitle'),
            message: th('vehiclesForSale.dialog.confirmCancelRequestMessage'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: th('vehiclesForSale.buttons.cancelRequest'),
            rejectLabel: th('vehiclesForSale.buttons.cancel'),
            accept: async () => {
                try {
                    const requestId = row.myRequestId ?? row.requestId;
                    if (!requestId) return;
                    const res = await cancelPurchaseRequest({ requestId, languageId }).unwrap();
                    if (res?.isSuccess || res?.isSuccessful) {
                        toastService.success(
                            res.message || th('vehiclesForSale.toast.requestCancelled')
                        );
                        refetch();
                    }
                } catch (err) {
                    void err;
                }
            }
        });
    };

    const handleCreateSuccess = () => {
        refetch();
    };

    const renderListingTab = ({ showDealerFilter }) => (
        <>
            <SaleListingsFilter
                filters={listingFilters}
                onApplyFilters={handleApplyListingFilters}
                onReset={handleResetListingFilters}
                showDealerFilter={showDealerFilter}
            />
            <SaleListingsTable
                data={listings}
                totalRecords={listingsTotal}
                loading={isLoading}
                pageNumber={listingFilters.pageNumber}
                pageSize={listingFilters.pageSize}
                onPageChange={handleListingPageChange}
                onViewDetail={handleViewDetail}
                onCreatePurchaseRequest={handleCreatePurchaseRequest}
                onCancelMyRequest={handleCancelMyRequest}
            />
        </>
    );

    const renderRequestsTab = () => (
        <>
            <MyPurchaseRequestsFilter
                filters={requestFilters}
                onApplyFilters={handleApplyRequestFilters}
                onReset={handleResetRequestFilters}
            />
            <MyPurchaseRequestsTable
                data={requests}
                totalRecords={requestsTotal}
                loading={isLoading}
                pageNumber={requestFilters.pageNumber}
                pageSize={requestFilters.pageSize}
                onPageChange={handleRequestPageChange}
                onCancelRequest={handleCancelMyRequest}
            />
        </>
    );

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-3">
                <h3>{th('vehiclesForSale.pageTitle')}</h3>
                <Button
                    icon="pi pi-refresh"
                    label={th('vehiclesForSale.buttons.refresh')}
                    onClick={refetch}
                />
            </div>

            <TabView
                activeIndex={activeTabIndex}
                onTabChange={(e) => setActiveTabIndex(e.index)}
            >
                {tabConfig.map((tab) => (
                    <TabPanel
                        key={tab.key}
                        header={tab.label}
                        leftIcon={`${tab.icon} mr-2`}
                    >
                        {tab.key === TAB_KEYS.ALL && renderListingTab({ showDealerFilter: true })}
                        {tab.key === TAB_KEYS.MINE && renderListingTab({ showDealerFilter: false })}
                        {tab.key === TAB_KEYS.REQUESTS && renderRequestsTab()}
                    </TabPanel>
                ))}
            </TabView>

            <ListingDetailDialog
                visible={!!detailListingId}
                listingId={detailListingId}
                onHide={handleDetailClose}
                onActionComplete={refetch}
            />

            <CreatePurchaseRequestDialog
                visible={!!purchaseTarget}
                listing={purchaseTarget}
                onHide={handleCreateClose}
                onSuccess={handleCreateSuccess}
            />

            <ConfirmDialog />
        </div>
    );
};

export default VehiclesForSalePage;
